import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { objectSharesKeys } from './objectShares.queries';
import { sharedObjectsKeys } from './sharedObjects.queries';
import type { ObjectShareCreateRequest } from '../../types/api';

// STD-015: write actions for the file/folder-sharing domain (#1182 Stage 4
// slice 4). apiService is imported here (a *.mutations.ts file) per STD-016.
//
// Both mutations suppress the global error toast (`meta.skipGlobalErrorToast`)
// and let the dialog render the failure inline. `SHARE_NO_OBJECTS` is a refusal
// the user resolves by changing the selection — a toast that vanishes after
// five seconds and points at nothing is the wrong surface for it.
//
// Both also invalidate TWO namespaces (#1205). `object-shares` holds share ROWS;
// `shared-objects` holds the objects those rows make REACHABLE, and the counter-
// party's "Shared with me" listing is drawn from the latter. Granting or
// revoking changes both — a folder grant adds one row and reaches every file
// under it — so invalidating only the rows would leave the listing that the
// grant actually exists FOR showing a stale answer.

/** Both listings a grant or revoke can change: the share rows, and the reach. */
const SHARE_QUERY_NAMESPACES = [objectSharesKeys.all, sharedObjectsKeys.all];

/**
 * Grant read/write on files or folders to another organization.
 *
 * Invalidates the whole `object-shares` namespace rather than the one list key.
 * A folder grant changes what the per-object list says for every descendant —
 * reach is resolved by an ancestor walk, so there is no enumerable set of keys
 * to invalidate precisely, and pretending otherwise would leave stale rows on
 * screen after the one operation most likely to surprise the user.
 */
export function useGrantObjectSharesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (body: ObjectShareCreateRequest) => apiService.grantObjectShares(body),
    onSuccess: () => {
      for (const queryKey of SHARE_QUERY_NAMESPACES) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/**
 * Revoke a share by stamping `revoked_at`.
 *
 * Same broad invalidation, for a sharper version of the same reason: revoking a
 * folder share withdraws the entire subtree in one row, so every descendant's
 * view of "am I shared?" changes at once.
 */
export function useRevokeObjectShareMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (shareId: string) => apiService.revokeObjectShare(shareId),
    onSuccess: () => {
      for (const queryKey of SHARE_QUERY_NAMESPACES) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
